import { ChangeDetectorRef, Component, inject, OnInit, DestroyRef } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { Permission } from 'app/core/enums';
import { SavedFilter } from 'app/core/models/account.types';
import { AuthService } from 'app/core/services/auth.service';
import { PipelineAddComponent } from 'app/modules/projects/modals/pipeline-add/pipeline-add.component';
import { Pipeline } from 'app/modules/projects/projects.types';
import { Pipelines } from 'app/state/pipelines/action';
import { PipelinesState } from 'app/state/pipelines/state';
import { SavedFilters } from 'app/state/saved-filters/action';
import { SavedFiltersState } from 'app/state/saved-filters/state';
import { combineLatest, filter, Observable } from 'rxjs';
import { TSTreeAction, TSTreeNode } from 'tsui';
import { UserState } from '@state/user/state';

@Component({
  selector: 'app-deals-sidebar',
  templateUrl: './deals-sidebar.component.html',
  styleUrls: ['./deals-sidebar.component.css'],
})
export class DealsSidebarComponent implements OnInit {
  pipelines: Pipeline[];
  viewsGroups: any[];
  savedFilters: SavedFilter[] = [];
  selectedPipeline: Pipeline;
  currentUrl: string;
  loading = true;

  readonly router = inject(Router);
  private store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  pipelines$: Observable<Pipeline[]> = inject(Store).select(PipelinesState.getPipelines);
  pipelineGroups$: Observable<any[]> = inject(Store).select(PipelinesState.getPipelinesGroups);
  savedFilters$: Observable<SavedFilter[]> = inject(Store).select(SavedFiltersState.getVisibleFilters);

  tsTreeActions: TSTreeAction[] = [];
  sidebarItems: TSTreeNode[] = [];
  sidebarSavedFiltersItems: TSTreeNode[] = [];

  constructor(
    public matDialog: MatDialog,
    private readonly authService: AuthService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  canManagePipeline(): boolean {
    // Mapped to new permission DEALS_PUBLIC_CUSTOM_VIEWS
    return this.store.selectSnapshot(UserState.hasPermission(Permission.PIPELINE_MANAGE));
  }

  ngOnInit() {
    if (this.canManagePipeline()) {
      this.tsTreeActions = [
        {
          key: 'new-group',
          label: 'Add new group',
          icon: 'add_circle_outline',
        },
        {
          key: 'project-sidebar-settings',
          label: 'Settings',
          icon: 'settings',
        },
      ];
    }

    this.store.dispatch([Pipelines.Get, Pipelines.GetGroups, SavedFilters.Get]);

    this.currentUrl = this.router.url.split('?')[0];

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url.split('?')[0];
        this.setSidebarItems();
        this.setSavedFilters();
        this.navigateToPipeline();
      }
    });

    combineLatest([this.pipelines$, this.pipelineGroups$, this.savedFilters$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([pipelines, groups, savedFilters]) => {
          return (
            Array.isArray(pipelines) && Array.isArray(groups) && Array.isArray(savedFilters) && !!pipelines && !!groups
          );
        }),
      )
      .subscribe(([pipelines, groups, savedFilters]) => {
        this.pipelines = pipelines || [];
        this.viewsGroups = groups || [];
        this.savedFilters = savedFilters;
        this.setSidebarItems();
        this.setSavedFilters();
        this.navigateToPipeline();
      });
  }

  private setSidebarItems(): void {
    this.sidebarItems = [
      ...(this.viewsGroups?.map((viewGroup) => {
        const filteredPipelines = this.pipelines?.filter((p) => {
          return viewGroup.members.find((member) => member.groupable_id == p.id) !== undefined;
        });

        const sidebarItem: TSTreeNode = {
          id: +viewGroup.id,
          name: viewGroup.label,
          expandable: true,
          children: filteredPipelines.map((pipeline) => this.getSidebarMenuItem(pipeline)),
        };

        return sidebarItem;
      }) || []),
    ];

    const items = this.pipelines
      ?.filter((p) => !p.object_group_id)
      ?.map((pipeline) => this.getSidebarMenuItem(pipeline));

    if (items?.length) this.sidebarItems.push(...items);
    this.loading = false;
    this.changeDetector.detectChanges();
  }

  private setSavedFilters(): void {
    this.sidebarSavedFiltersItems = this.savedFilters.map((savedFilter) => {
      return {
        id: savedFilter.id.toString(),
        name: savedFilter.name,
        icon: 'play_circle_filled',
        link: `${this.currentUrl}?filterId=${savedFilter.id}`,
      };
    });

    this.sidebarSavedFiltersItems.push({
      id: 'clear-filter',
      name: 'Clear Filters',
      icon: 'remove_circle',
      link: `${this.currentUrl}?filterId=reset`,
    });
  }

  private openAddPipelineModal(parent): void {
    let modalData;

    if (parent) {
      modalData = { pipelines: this.pipelines, creatingView: true, selectedViewGroup: parent };
    } else {
      modalData = { pipelines: this.pipelines, creatingGroup: true };
    }

    this.matDialog
      .open(PipelineAddComponent, {
        data: modalData,
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => this.store.dispatch([Pipelines.Get, Pipelines.GetGroups]));
  }

  onNewItem(action: TSTreeAction): void {
    this.openAddPipelineModal(action.meta.parent);
  }

  onActionClicked(action: TSTreeAction): void {
    const actionMap = {
      'new-item': () => this.openAddPipelineModal(action.meta.parent),
      'new-group': () => this.openAddPipelineModal(null),
      'project-sidebar-settings': () => this.router.navigateByUrl('/projects/settings'),
    };

    actionMap[action.key] && actionMap[action.key]();
  }

  private getSidebarMenuItem(pipeline: Pipeline): TSTreeNode {
    return {
      id: pipeline.id.toString(),
      name: pipeline.name,
      badge: {
        value: pipeline.num_projects_user_has_access_to || pipeline.num_projects,
        type: 'transparent',
      },
      icon: pipeline.pipeline_type === 'static' ? 'list_alt' : 'low_priority',
      iconTooltip: pipeline.pipeline_type === 'static' ? 'Static' : 'Dynamic',
      link: '/projects/' + pipeline.id,
    };
  }

  disableSavedViewItem = (_): boolean => {
    return this.selectedPipeline?.pipeline_type === 'dynamic';
  };

  checkViewIsActive = (node): boolean => {
    return !!node.link && this.currentUrl.startsWith(node.link);
  };

  checkSavedFilterIsActive = (node) => {
    const url = this.router.url.split('?')[1];
    return node.link.includes(url);
  };

  private navigateToPipeline(): void {
    if (this.currentUrl === '/projects') {
      const lastAccessedPipeline = localStorage.getItem('projectPipeline');
      if (lastAccessedPipeline) {
        this.router.navigateByUrl('/projects/' + lastAccessedPipeline);
        return;
      }
      const firstPipeline = this.pipelines.sort((a, b) => a.id - b.id).find((p) => p.pipeline_type === 'static');
      if (firstPipeline) {
        this.router.navigateByUrl('/projects/' + firstPipeline.id);
      }
    }
  }
}
